import { Button, Dropdown, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';

import { CamDataStockRow } from '../../../interfaces/Fusion360SetupData.interfaces';
import { useGetCamDataStockQuery } from '../../../redux/api/order';
import { LENGTH_PARAMETER_UNITS, fromInches } from './units';

/** CAM stock axes (inches) → robot program stock parameters. */
const AXIS_TO_PARAMETER = [
	['stock_x', 'width'],
	['stock_y', 'length'],
	['stock_z', 'partHeight'],
] as const;

export type CamStockValues = Partial<Record<'width' | 'length' | 'partHeight', number>>;

const isNumber = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value);

const stockValues = (row: CamDataStockRow): CamStockValues => {
	const values: CamStockValues = {};
	for (const [axis, key] of AXIS_TO_PARAMETER) {
		const inches = row[axis];
		if (isNumber(inches)) {
			// Rounded so 2 in lands on 50.8, not 50.800000000000004.
			values[key] = Number(fromInches(inches, LENGTH_PARAMETER_UNITS[key]).toFixed(4));
		}
	}
	return values;
};

const rowLabel = (row: CamDataStockRow) =>
	row.machine_model_name ?? row.machine_name ?? 'Fusion 360 CAM export';

interface CamStockAutofillProps {
	orderLineItemId: string;
	disabled?: boolean;
	onApply: (values: CamStockValues, source: string) => void;
}

const CamStockAutofill = ({ orderLineItemId, disabled, onApply }: CamStockAutofillProps) => {
	const { data = [], isFetching } = useGetCamDataStockQuery({ id: orderLineItemId });
	const usable = data.filter((row) => Object.keys(stockValues(row)).length > 0);

	if (!isFetching && usable.length === 0) {
		return null;
	}

	const apply = (row: CamDataStockRow) => onApply(stockValues(row), rowLabel(row));
	const tooltip = 'Fill Stock Width, Length and Height from the Fusion 360 CAM stock (X, Y, Z)';

	if (usable.length > 1) {
		return (
			<Dropdown
				disabled={disabled}
				trigger={['click']}
				menu={{
					items: usable.map((row) => ({ key: row.id, label: rowLabel(row) })),
					onClick: ({ key }) => {
						const row = usable.find((candidate) => candidate.id === key);
						if (row) apply(row);
					},
				}}
			>
				<Tooltip title={tooltip}>
					<Button disabled={disabled}>
						Autofill from Fusion 360 CAM <DownOutlined />
					</Button>
				</Tooltip>
			</Dropdown>
		);
	}

	return (
		<Tooltip title={tooltip}>
			<Button
				loading={isFetching}
				disabled={disabled || usable.length === 0}
				onClick={() => apply(usable[0])}
			>
				Autofill from Fusion 360 CAM
			</Button>
		</Tooltip>
	);
};

export default CamStockAutofill;
